/**
 * User Profile Edit Route - React Router v7
 *
 * Route: /user/profile/edit
 * Description: Edit user profile (personal data, avatar, password)
 */
import type {LoaderFunctionArgs} from 'react-router-dom'
/* eslint-disable @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-throw-literal, @typescript-eslint/strict-boolean-expressions, @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/no-unused-vars */
import {useLoaderData} from 'react-router-dom'

import UserProfileEdit from './index.js'

// Import styles
import './styles/index.scss'

/**
 * Loader for User Profile Edit page
 * Fetches user data and SEO metadata
 */
export async function loader({context, request}: LoaderFunctionArgs) {
  const {domain, i18n} = context
  const {culture} = i18n
  const url = new URL(request.url)
  const path = url.pathname

  try {
    // Get page name
    const {pageName} = await domain.get('get_page_name_page_use_case').execute({path})

    // Fetch in parallel: SEO data + user data
    const [seoDataResult, userDataResult] = await Promise.allSettled([
      domain.get('get_seo_data_use_case').execute({culture, pageName}),
      domain.get('get_user_by_id_user_use_case').execute()
    ])

    const helmet = seoDataResult.status === 'fulfilled' ? seoDataResult.value?.[1]?.head || {} : {}
    const userData = userDataResult.status === 'fulfilled' ? userDataResult.value?.[1] : null

    if (seoDataResult.status === 'rejected') {
      console.error('[UserProfileEdit] SEO data error:', seoDataResult.reason)
    }
    if (userDataResult.status === 'rejected') {
      console.error('[UserProfileEdit] User data error:', userDataResult.reason)
    }

    return {
      helmet,
      pageName,
      userData
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[UserProfileEdit] Loader error:', error)
    return {
      helmet: {},
      pageName: 'user_profile_edit',
      userData: null
    }
  }
}

/**
 * Component wrapper
 * Uses existing UserProfileEdit component
 */
export function Component() {
  const data = useLoaderData()
  return <UserProfileEdit {...data} />
}

export default {loader, Component}
